import { useEffect, useState, useMemo } from "react";
import { Building2, LayoutDashboard, FileText, Receipt, Workflow, Clock, StickyNote, ChevronDown, ChevronUp, Copy, Pencil } from "lucide-react";
import { Users } from "lucide-react";
import { GenericEntityWorkspacePage } from "./shared/GenericEntityWorkspacePage";
import { clientBoardConfig } from "./shared/entityWorkspaceBoardConfigs";
import { Modal } from "../primitives/Modal";
import { Drawer } from "../primitives/Drawer";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Button } from "../primitives/Button";
import { IconButton } from "../primitives/IconButton";
import { createFarm, listFarms, getFarm, updateFarm } from "../../api/admin";
import { ClientStrategicHeader } from "../components/ClientStrategicHeader/ClientStrategicHeader";
import { getClientsSelectedIdFromHash } from "../../routing";
import { cn } from "../utils/cn";
import { COUNTRIES, getProvincesForCountry } from "../utils/countries";
import type { EntityWorkspaceBoardConfig, EntityListItem, EntityWorkspaceBoardEntity } from "./shared/entityWorkspaceBoardConfigs";
import type { EntityHeaderPatternProps } from "../patterns/EntityHeaderPattern";
import type { OperationBoardPatternProps } from "../patterns/OperationBoardPattern";

export function ClientsListPage({ userName = "Utilisateur" }: { userName?: string }) {
  const [farms, setFarms] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "QC - Québec",
    postal_code: "",
    country: "CA",
    lifecycle_status: "lead",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "QC - Québec",
    postalCode: "",
    country: "CA",
    contactFirst: "",
    contactLast: "",
    contactEmail: "",
    contactPhone: "",
    lifecycle: "lead",
  });

  const availableProvinces = useMemo(() => {
    return getProvincesForCountry(form.country);
  }, [form.country]);

  const provinceOptions = useMemo(() => {
    return availableProvinces.map((p) => `${p.code} - ${p.name}`);
  }, [availableProvinces]);

  const countryOptions = useMemo(() => {
    return COUNTRIES.map((c) => `${c.code} - ${c.name}`);
  }, []);

  const editProvinces = useMemo(() => getProvincesForCountry(editForm.country), [editForm.country]);
  const editProvinceOptions = useMemo(() => editProvinces.map((p) => `${p.code} - ${p.name}`), [editProvinces]);

  // Load farm into edit form when drawer opens
  useEffect(() => {
    if (!editDrawerOpen || !selectedId) return;
    setEditLoading(true);
    setEditErrors({});
    getFarm(selectedId)
      .then((data) => {
        const provinces = getProvincesForCountry(data.organization.country || "CA");
        const provinceLabel = data.organization.province
          ? provinces.find((p) => p.code === data.organization.province)
            ? `${data.organization.province} - ${provinces.find((p) => p.code === data.organization.province)!.name}`
            : data.organization.province
          : provinces.length ? `${provinces[0].code} - ${provinces[0].name}` : "";
        setEditForm({
          name: data.organization.name || "",
          email: data.organization.email || "",
          phone: data.organization.phone || "",
          address_line1: data.organization.address_line1 || "",
          address_line2: data.organization.address_line2 || "",
          city: data.organization.city || "",
          province: provinceLabel || "QC - Québec",
          postal_code: data.organization.postal_code || "",
          country: data.organization.country || "CA",
          lifecycle_status: data.profile.lifecycle_status || "lead",
        });
      })
      .catch(() => setEditErrors({ form: "Impossible de charger le client." }))
      .finally(() => setEditLoading(false));
  }, [editDrawerOpen, selectedId]);

  const handleEditSave = async () => {
    if (!selectedId) return;
    const newErrors: Record<string, string> = {};
    if (!editForm.name.trim()) newErrors.name = "Le nom est requis";
    if (Object.keys(newErrors).length > 0) {
      setEditErrors(newErrors);
      return;
    }
    setEditErrors({});
    setEditSaving(true);
    try {
      const provinceCode = editForm.province.split(" - ")[0] || editForm.province;
      await updateFarm(selectedId, {
        organization: {
          name: editForm.name,
          email: editForm.email || undefined,
          phone: editForm.phone || undefined,
          address_line1: editForm.address_line1 || undefined,
          address_line2: editForm.address_line2 || undefined,
          city: editForm.city || undefined,
          province: provinceCode || undefined,
          postal_code: editForm.postal_code || undefined,
          country: editForm.country || undefined,
        },
        profile: { lifecycle_status: editForm.lifecycle_status },
      });
      setEditDrawerOpen(false);
      handleFarmUpdate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setEditErrors({ form: msg });
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false);
    setEditErrors({});
  };

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    listFarms()
      .then((data) => {
        if (!mounted) return;
        const items = data.items || [];
        setFarms(items);
        if (items.length > 0) {
          const fromHash = getClientsSelectedIdFromHash();
          const exists = fromHash && items.some((f: { organization: { id: string } }) => f.organization.id === fromHash);
          setSelectedId((prev) => (exists ? fromHash! : prev || items[0].organization.id));
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load farms:", error);
        alert(`Erreur lors du chargement des clients: ${error.message || error}\n\nVérifiez la console pour plus de détails.`);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleCreate = async () => {
    // Validation des champs requis
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom de la ferme est requis";
    }
    if (!form.contactFirst.trim()) {
      newErrors.contactFirst = "Le prénom du contact est requis";
    }
    if (!form.contactLast.trim()) {
      newErrors.contactLast = "Le nom du contact est requis";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    try {
      const provinceCode = form.province.split(" - ")[0] || form.province;
      const createdFarm = await createFarm({
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        province: provinceCode,
        postalCode: form.postalCode,
        country: form.country,
        contact_first_name: form.contactFirst,
        contact_last_name: form.contactLast,
        contact_email: form.contactEmail,
        contact_phone: form.contactPhone,
        lifecycle_status: form.lifecycle,
      });
      // Reset form
      setForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        province: "QC",
        postalCode: "",
        country: "CA",
        contactFirst: "",
        contactLast: "",
        contactEmail: "",
        contactPhone: "",
        lifecycle: "lead",
      });
      setOpen(false);
      setErrors({});
      // Optimistic update: append created farm without reloading full list.
      setFarms((prev) => [...prev, createdFarm]);
      setSelectedId(createdFarm.organization.id);
    } catch (error) {
      console.error("Failed to create client:", error);
      const message = error instanceof Error ? error.message : String(error);
      alert(`Erreur lors de la création du client: ${message}`);
    }
  };

  const handleCancel = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      province: "QC",
      postalCode: "",
      country: "CA",
      contactFirst: "",
      contactLast: "",
      contactEmail: "",
      lifecycle: "lead",
    });
    setErrors({});
    setOpen(false);
  };

  const handleCountryChange = (value: string) => {
    const countryCode = value.split(" - ")[0] || value;
    const newProvinces = getProvincesForCountry(countryCode);
    const defaultProvince = newProvinces.length > 0 ? `${newProvinces[0].code} - ${newProvinces[0].name}` : "";
    setForm({
      ...form,
      country: countryCode,
      province: defaultProvince,
    });
  };

  const entityListItems: EntityListItem[] = useMemo(() => {
    return farms.map((item) => ({
      id: item.organization.id,
      title: item.organization.name,
      subtitle: item.organization.city || item.organization.province || "",
      info: item.primary_contact 
        ? `${item.primary_contact.person.first_name} ${item.primary_contact.person.last_name}`
        : "Aucun contact",
      icon: <Building2 size={16} strokeWidth={1.5} />,
      status: {
        label: item.profile.lifecycle_status === "active" ? "Actif" 
          : item.profile.lifecycle_status === "lead" ? "Prospect"
          : item.profile.lifecycle_status === "on_hold" ? "En attente"
          : "Inactif",
        tone: item.profile.lifecycle_status === "active" ? "success"
          : item.profile.lifecycle_status === "lead" ? "warning"
          : item.profile.lifecycle_status === "on_hold" ? "warning"
          : "neutral",
      },
    }));
  }, [farms]);

  const entities: EntityWorkspaceBoardEntity[] = useMemo(() => {
    return farms.map((item) => {
      const header: EntityHeaderPatternProps = {
        title: item.organization.name,
        metaItems: [
          { label: "Statut", value: item.profile.lifecycle_status === "active" ? "Actif" : item.profile.lifecycle_status, tone: "default" },
          { label: "Ville", value: item.organization.city || "-", tone: "muted" },
          { label: "Province", value: item.organization.province || "-", tone: "muted" },
        ],
        status: {
          label: item.profile.lifecycle_status === "active" ? "Actif" : item.profile.lifecycle_status,
          tone: item.profile.lifecycle_status === "active" ? "success" : "neutral",
        },
        primaryAction: { label: "Nouvelle soumission" },
        secondaryActions: [{ label: "Voir dossier" }, { label: "Archiver" }],
      };

      const board: OperationBoardPatternProps = {
        title: "Soumissions",
        stickyHeader: true,
        items: [], // TODO: Load submissions when available
        rowActions: [{ label: "Voir" }, { label: "Archiver" }],
      };

      return {
        id: item.organization.id,
        header,
        board,
      };
    });
  }, [farms]);

  const selectedFarm = useMemo(() => {
    return farms.find((item) => item.organization.id === selectedId);
  }, [farms, selectedId]);

  const headerSlot = useMemo(() => {
    if (!selectedFarm) return null;
    
    const headerData = {
      client: {
        id: selectedFarm.organization.id,
        name: selectedFarm.organization.name,
        relationship: (selectedFarm.profile.lifecycle_status === "active" ? "active" : 
                       selectedFarm.profile.lifecycle_status === "on_hold" ? "paused" : 
                       "inactive") as "active" | "inactive" | "paused",
        region: selectedFarm.organization.province || undefined,
        phone: selectedFarm.organization.phone || undefined,
        email: selectedFarm.organization.email || undefined,
        xlHubActive: false, // TODO: Get from API when available
        score10: undefined, // TODO: Get from API when available
        grade: undefined, // TODO: Get from API when available
      },
      commercial: {
        interest: "unknown" as const,
        quotes: [],
        notes: [],
      },
      operations: {
        items: [],
      },
      finance: {
        invoices: [],
      },
      actionsNeeded: undefined,
    };

    const goToNewQuote = () => {
      const params = new URLSearchParams();
      if (selectedId) params.set("clientId", selectedId);
      if (selectedFarm?.organization?.name)
        params.set("clientName", selectedFarm.organization.name);
      window.location.hash = `#/soumission${params.toString() ? `?${params.toString()}` : ""}`;
    };

    return (
      <div className="rounded-md border border-border bg-surface shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
          <span className="text-sm font-semibold text-text">Vue stratégique</span>
          <div className="flex items-center gap-2">
            <Button
              label="Modifier"
              intent="default"
              size="sm"
              icon={<Pencil size={14} strokeWidth={1.5} />}
              onClick={() => setEditDrawerOpen(true)}
            />
            <IconButton
              icon={headerCollapsed ? <ChevronDown size={16} strokeWidth={1.5} /> : <ChevronUp size={16} strokeWidth={1.5} />}
              ariaLabel={headerCollapsed ? "Développer" : "Réduire"}
              intent="ghost"
              size="sm"
              onClick={() => setHeaderCollapsed(!headerCollapsed)}
            />
          </div>
        </div>
        <div className={cn("transition-all duration-200 ease-out", headerCollapsed ? "max-h-0 overflow-hidden" : "max-h-[2000px]")}>
          <div className="p-4">
            <ClientStrategicHeader {...headerData} onNewQuote={goToNewQuote} />
          </div>
        </div>
      </div>
    );
  }, [selectedFarm, headerCollapsed, selectedId]);

  const handleFarmUpdate = () => {
    // Refresh the farm list after update
    listFarms()
      .then((data) => {
        const items = data.items || [];
        setFarms(items);
        if (selectedId && !items.find((item) => item.organization.id === selectedId)) {
          // If selected farm was deleted/archived, select first available
          if (items.length > 0) {
            setSelectedId(items[0].organization.id);
          } else {
            setSelectedId(null);
          }
        }
      })
      .catch((error) => {
        console.error("Failed to refresh farms:", error);
      });
  };

  const config: EntityWorkspaceBoardConfig = useMemo(() => ({
    ...clientBoardConfig,
    entityListItems,
    entities,
    headerSlot,
  }), [entityListItems, entities, headerSlot]);

  // Nouvelle soumission : navigation vers le Quote Builder (nouveau flux 8 sections)
  useEffect(() => {
    (window as any).__openSubmissionModal = () => {
      const params = new URLSearchParams();
      if (selectedId) params.set("clientId", selectedId);
      if (selectedFarm?.organization?.name)
        params.set("clientName", selectedFarm.organization.name);
      window.location.hash = `#/soumission${params.toString() ? `?${params.toString()}` : ""}`;
    };
    return () => {
      delete (window as any).__openSubmissionModal;
    };
  }, [selectedId, selectedFarm]);

  return (
    <>
      <div className="flex flex-col h-screen overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <GenericEntityWorkspacePage
            config={config}
            userName={userName}
            items={entityListItems}
            selectedId={selectedId}
            onSelect={setSelectedId}
            addActionLabel="+ Ajouter"
            onAddAction={() => setOpen(true)}
            onEntityUpdate={handleFarmUpdate}
            containerFluid={true}
          />
        </div>
      </div>
      <Modal title="Créer un client" open={open} onClose={handleCancel} showDefaultButtons={false} showCloseButton={true} size="xl">
        <div className="space-y-3">
          <div>
            <Input 
              label="Nom de la ferme" 
              placeholder="Ferme des Trois Rivières" 
              value={form.name} 
              onChange={(event) => {
                setForm({ ...form, name: event.target.value });
                if (errors.name) {
                  setErrors({ ...errors, name: "" });
                }
              }}
              state={errors.name ? "error" : "default"}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-danger">{errors.name}</p>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Email" placeholder="email@ferme.com" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
            <Input label="Téléphone" placeholder="(xxx) xxx-xxxx" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          </div>
          <Input label="Adresse" placeholder="123 rue Principale" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Ville" placeholder="Ville" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
            <Input label="Code postal" placeholder="A1A 1A1" value={form.postalCode} onChange={(event) => setForm({ ...form, postalCode: event.target.value })} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label="Pays"
              options={countryOptions}
              value={`${form.country} - ${COUNTRIES.find((c) => c.code === form.country)?.name || ""}`}
              onChange={handleCountryChange}
            />
            <Select
              label="Province/État"
              options={provinceOptions}
              value={form.province}
              onChange={(value) => setForm({ ...form, province: value })}
            />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Input 
                label="Contact prénom" 
                placeholder="Prénom" 
                value={form.contactFirst} 
                onChange={(event) => {
                  setForm({ ...form, contactFirst: event.target.value });
                  if (errors.contactFirst) {
                    setErrors({ ...errors, contactFirst: "" });
                  }
                }}
                state={errors.contactFirst ? "error" : "default"}
              />
              {errors.contactFirst && (
                <p className="mt-1 text-xs text-danger">{errors.contactFirst}</p>
              )}
            </div>
            <div>
              <Input 
                label="Contact nom" 
                placeholder="Nom" 
                value={form.contactLast} 
                onChange={(event) => {
                  setForm({ ...form, contactLast: event.target.value });
                  if (errors.contactLast) {
                    setErrors({ ...errors, contactLast: "" });
                  }
                }}
                state={errors.contactLast ? "error" : "default"}
              />
              {errors.contactLast && (
                <p className="mt-1 text-xs text-danger">{errors.contactLast}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-muted">Contact email</label>
            <div className="flex items-center gap-2">
              <input
                className="h-10 flex-1 rounded-md border border-border bg-surface px-3 text-text"
                placeholder="contact@ferme.com"
                value={form.contactEmail}
                onChange={(event) => setForm({ ...form, contactEmail: event.target.value })}
              />
              {form.email && (
                <IconButton
                  icon={<Copy size={14} strokeWidth={1.5} />}
                  ariaLabel="Copier l'email de la ferme"
                  intent="ghost"
                  size="sm"
                  onClick={() => setForm({ ...form, contactEmail: form.email })}
                />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-muted">Contact téléphone</label>
            <div className="flex items-center gap-2">
              <input
                className="h-10 flex-1 rounded-md border border-border bg-surface px-3 text-text"
                placeholder="(xxx) xxx-xxxx"
                value={form.contactPhone}
                onChange={(event) => setForm({ ...form, contactPhone: event.target.value })}
              />
              {form.phone && (
                <IconButton
                  icon={<Copy size={14} strokeWidth={1.5} />}
                  ariaLabel="Copier le téléphone de la ferme"
                  intent="ghost"
                  size="sm"
                  onClick={() => setForm({ ...form, contactPhone: form.phone })}
                />
              )}
            </div>
          </div>
          <Select
            label="Lifecycle"
            options={["lead", "active", "on_hold", "churned"]}
            value={form.lifecycle}
            onChange={(value) => setForm({ ...form, lifecycle: value })}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button label="Annuler" intent="default" size="sm" onClick={handleCancel} />
            <Button label="Créer" intent="primary" size="sm" onClick={handleCreate} />
          </div>
        </div>
      </Modal>

      <Drawer
        open={editDrawerOpen}
        onClose={handleEditDrawerClose}
        title="Modifier le client"
        size="xl"
      >
        <div className="space-y-4">
          {editErrors.form && (
            <p className="text-sm text-danger">{editErrors.form}</p>
          )}
          {editLoading ? (
            <p className="text-sm text-muted">Chargement...</p>
          ) : (
            <>
              <Input
                label="Nom de la ferme"
                placeholder="Ferme des Trois Rivières"
                value={editForm.name}
                onChange={(e) => {
                  setEditForm({ ...editForm, name: e.target.value });
                  if (editErrors.name) setEditErrors({ ...editErrors, name: "" });
                }}
                state={editErrors.name ? "error" : "default"}
              />
              {editErrors.name && <p className="mt-1 text-xs text-danger">{editErrors.name}</p>}
              <div className="grid gap-3 md:grid-cols-2">
                <Input label="Email" placeholder="email@ferme.com" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                <Input label="Téléphone" placeholder="(xxx) xxx-xxxx" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <Input label="Adresse" placeholder="123 rue Principale" value={editForm.address_line1} onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })} />
              <Input label="Adresse (ligne 2)" placeholder="App., suite" value={editForm.address_line2} onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })} />
              <div className="grid gap-3 md:grid-cols-2">
                <Input label="Ville" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                <Input label="Code postal" value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Select
                  label="Pays"
                  options={countryOptions}
                  value={`${editForm.country} - ${COUNTRIES.find((c) => c.code === editForm.country)?.name ?? ""}`}
                  onChange={(value) => {
                    const code = value.split(" - ")[0] || value;
                    const provs = getProvincesForCountry(code);
                    setEditForm({
                      ...editForm,
                      country: code,
                      province: provs.length ? `${provs[0].code} - ${provs[0].name}` : "",
                    });
                  }}
                />
                <Select
                  label="Province/État"
                  options={editProvinceOptions}
                  value={editForm.province}
                  onChange={(value) => setEditForm({ ...editForm, province: value })}
                />
              </div>
              <Select
                label="Statut"
                options={["lead", "active", "on_hold", "churned"]}
                value={editForm.lifecycle_status}
                onChange={(value) => setEditForm({ ...editForm, lifecycle_status: value })}
              />
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button label="Annuler" intent="default" size="sm" onClick={handleEditDrawerClose} />
                <Button label="Enregistrer" intent="primary" size="sm" onClick={handleEditSave} disabled={editSaving} />
              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
}
